import React, { useRef, useEffect } from 'react';
import Swipe from 'react-easy-swipe';
import { get } from 'helpers/utilities';
import Icon from 'components/Icon';
import Image from 'components/Image';
import EventHandler from 'helpers/EventHandler';
import { EVENT, breakpoints } from 'config/constants';
import useThrottle from '../../hooks/useThrottle';

import styles from './styles.scss';

const Nearby3Up = props => {
  const { nearbyTitle, nearbyDescription, nearbyFeatures } = props;

  const throttle = useThrottle();
  const cardsRef = useRef();
  const originalX = useRef(0);
  const currentX = useRef(0);
  const index = useRef(0);
  const scrollDirection = useRef();

  const eventResize = useRef();

  const getCurrentTranslateX = () => {
    const currentTransform = window.getComputedStyle(cardsRef.current).transform;
    return currentTransform !== 'none' ? parseFloat(currentTransform.match(/[0-9-.]+/g)[4]) : 0;
  };

  const getCurrentMargin = () => {
    const currentMargin = window.getComputedStyle(cardsRef.current.children[1]).marginLeft;
    return currentMargin !== '0px' ? parseFloat(currentMargin) : 0;
  };

  const getCardWidth = () => cardsRef.current.children[0].clientWidth + getCurrentMargin();

  const currentIndex = (idx = 0) => {
    const cards = nearbyFeatures;
    let card = 0;

    if (idx < 0) {
      card = 0;
    } else if (idx > cards.length - 1) {
      card = cards.length - 1;
    } else if (window.innerWidth >= breakpoints.tablet && idx >= cards.length - 1) {
      card = idx - 1;
    } else {
      card = idx;
    }

    return card;
  };

  const goToCard = idx => {
    const id = currentIndex(idx);
    index.current = id;
    originalX.current = id * getCardWidth() * -1;
    cardsRef.current.style.transform = `translateX(${originalX.current}px)`;
  };

  const resizeHandler = e => {
    if (e.width >= breakpoints.smallDesktop) {
      cardsRef.current.classList.remove(`${styles.nearby3Up__cards}--swipe`);
      cardsRef.current.style.transform = '';
    } else {
      goToCard(index.current);
    }
  };

  const onSwipeStart = () => {
    if (window.innerWidth >= breakpoints.smallDesktop) {
      return;
    }

    originalX.current = getCurrentTranslateX();
    currentX.current = 0;

    cardsRef.current.classList.remove(`${styles.nearby3Up__cards}--swipe`);
  };

  const onSwipeMove = e => {
    if (window.innerWidth >= breakpoints.smallDesktop) {
      return false;
    }

    if (!scrollDirection.current) {
      // threshold of 5 to determine
      // what scroll direction
      if (e.y > 5 || e.y < -5) {
        scrollDirection.current = 'vertical';
      } else {
        scrollDirection.current = 'horizontal';
      }
    }

    if (scrollDirection.current === 'horizontal') {
      currentX.current = e.x;
      cardsRef.current.style.transform = `translateX(${e.x + originalX.current}px)`;
      return true;
    }
    return false;
  };

  const onSwipeEnd = () => {
    scrollDirection.current = null;
    if (window.innerWidth >= breakpoints.smallDesktop) {
      return;
    }

    const direction = currentX.current > 0 ? -1 : 1;

    cardsRef.current.classList.add(`${styles.nearby3Up__cards}--swipe`);

    if (Math.abs(currentX.current) > window.innerWidth / 4) {
      goToCard((index.current += direction));
    } else {
      goToCard(index.current);
    }
  };

  useEffect(() => {
    eventResize.current = EventHandler.subscribe(EVENT.RESIZE, throttle(resizeHandler, 500));
    return () => EventHandler.unsubscribe(eventResize.current);
  }, []);

  return (
    <section className={styles.nearby3Up}>
      <div className={styles.nearby3Up__content}>
        <h2
          className={styles.nearby3Up__title}
          dangerouslySetInnerHTML={{ __html: get(nearbyTitle, 'content') }}
        />
        <div className={styles.nearby3Up__description}>
          <p>{nearbyDescription}</p>
        </div>
      </div>
      <Swipe
        ref={el => el && (cardsRef.current = el.swiper)}
        onSwipeStart={onSwipeStart}
        onSwipeMove={onSwipeMove}
        onSwipeEnd={onSwipeEnd}
        className={styles.nearby3Up__cards}>
        {nearbyFeatures.map((feature, idx) => (
          <article key={idx} className={styles.nearby3Up__card}>
            <a
              href={feature.mapUrl}
              className={styles.nearby3Up__card__link}
              title={`${feature.topic} | ${feature.featureName}`}
              target="_blank"
              rel="noopener noreferrer">{`${feature.topic} / ${feature.featureName}`}</a>
            <h3 className={styles.nearby3Up__card__heading}>
              <Icon name="location" classes={styles.nearby3Up__card__icon} />
              <span className={styles.nearby3Up__card__topic}>{feature.topic}</span>
              <span className={styles.nearby3Up__card__name}>{` | ${feature.featureName}`}</span>
            </h3>
            <div className={styles.nearby3Up__card__image}>
              <Image
                src={get(feature, 'image[0].url')}
                className={styles.nearby3Up__card__img}
                alt={`Topic: ${get(feature, 'topic')}, Feature Name: ${get(
                  feature,
                  'featureName'
                )}`}
              />
            </div>
          </article>
        ))}
      </Swipe>
    </section>
  );
};

export default Nearby3Up;
